'use client';

import * as React from 'react';
import { useSession } from 'next-auth/react';
import { useSearchParams, usePathname, useRouter } from 'next/navigation';
import { NextAppProvider } from '@toolpad/core/nextjs';
import { signIn, signOut } from 'next-auth/react';
import Logo from '../../public/logo.svg';
import Image from 'next/image';

// Wrap signIn to always redirect to base URL instead of current page
const signInWithBaseRedirect = (providerId?: string, options?: { callbackUrl?: string; redirect?: boolean }) => {
    // Always override callbackUrl to base URL
    // Exclude redirect from options to avoid type issues, let NextAuth use default behavior
    const { redirect: _redirect, callbackUrl: _callbackUrl, ...restOptions } = options || {};
    
    return signIn(providerId, {
        ...restOptions,
        callbackUrl: '/', // Always redirect to base URL after sign in
    });
};

interface NavigationItem {
    segment: string;
    title: string;
}

interface DynamicNavigationProviderProps {
    children: React.ReactNode;
    session: any; // Initial session from server
}

export function DynamicNavigationProvider({ children, session: initialSession }: DynamicNavigationProviderProps) {
    const { data: session } = useSession();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const router = useRouter();
    
    // Use client session if available, otherwise fall back to initial session
    const currentSession = session || initialSession;
    
    // Wrap signOut to redirect to login page after signout
    const signOutWithRedirect = async (options?: { redirect?: boolean; callbackUrl?: string }) => {
        await signOut({ ...options, redirect: false });
        router.push('/auth/signin');
    };
    
    const AUTHENTICATION = {
        signIn: signInWithBaseRedirect,
        signOut: signOutWithRedirect,
    };
    
    const userRole = currentSession?.user?.role;
    const requiresActivation = (currentSession?.user as any)?.requiresActivation || false;
    
    // Check if we're in password reset mode
    const isResetMode = pathname === '/activate' && searchParams.get('resetToken') !== null;

    // Navigation for password reset flow
    const RESET_NAVIGATION: NavigationItem[] = [
        {
            segment: 'activate',
            title: 'Set New Password',
        },
    ];

    // Navigation for account activation flow
    const ACTIVATION_NAVIGATION: NavigationItem[] = [
        {
            segment: 'activate',
            title: 'Account Activation',
        },
    ];

    // Regular navigation based on role
    const NAVIGATION: NavigationItem[] = React.useMemo(() => {
        if (userRole === 'employee') {
            return [
                {
                    segment: 'asset-accounts',
                    title: 'Asset Accounts',
                },
                {
                    segment: 'user-account',
                    title: 'User Account',
                },
            ];
        } else if (userRole === 'compliance officer') {
            return [
                {
                    segment: 'employees',
                    title: 'Employees',
                },
                {
                    segment: 'transaction-export',
                    title: 'Transaction Export',
                },
                {
                    segment: 'user-account',
                    title: 'User Account',
                },
            ];
        }
        return [];
    }, [userRole]);

    // Determine final navigation based on mode
    const finalNavigation = React.useMemo(() => {
        if (isResetMode) {
            return RESET_NAVIGATION;
        }
        if (requiresActivation) {
            return ACTIVATION_NAVIGATION;
        }
        return NAVIGATION;
    }, [isResetMode, requiresActivation, NAVIGATION]);

    return (
        <NextAppProvider
            key={`nav-${userRole}-${requiresActivation}-${isResetMode}`} // Force re-render when mode changes
            branding={{
                title: 'Trade Guardian',
                logo: (
                    <Image
                        src={Logo}
                        alt='logo'
                        style={{
                            width: 'auto',
                        }}
                    />
                ),
            }}
            navigation={finalNavigation}
            session={currentSession}
            authentication={AUTHENTICATION}
        >
            {children}
        </NextAppProvider>
    );
}

